/**
 * The single navigation tree for the whole platform.
 *
 * One sidebar, every module. This is the only place routes, labels, icons and
 * permission keys are declared together, so the sidebar, the breadcrumbs, the
 * command palette and the route guards can never drift apart.
 *
 * `status` matters: the shell renders `ready` items only. A screen that has
 * not been brought across yet is declared here so the plan is visible, but it
 * is never shown as a clickable menu entry. The web app's `check:nav` script
 * fails the build if a ready entry has no route behind it.
 */
import { distributionNav } from './nav/distribution.js';
import { fieldNav, fieldSetupNav } from './nav/field.js';
const R = 'ready';
const P = 'planned';
/**
 * Where every screen is declared.
 *
 * This is not the menu. It is the pile the menu is built from: each entry's
 * address, its icon, the permission that gates it and the words the search
 * box matches on, written down once. The shape people navigate by is composed
 * out of it below, which means a screen can be moved between sections without
 * its definition being touched, and nothing can be moved without the compiler
 * noticing if it goes missing.
 *
 * The grouping here is the old one, kept only because it is a convenient way
 * to keep related declarations together. Nobody sees it.
 */
const SOURCE_SECTIONS = [
    // -------------------------------------------------------------------------
    // What is going on, and the two screens everybody shares.
    {
        label: 'Overview',
        items: [
            // Two different screens, and the menu used to name only the first and
            // call it the dashboard. `/` is the list of things you can do; the
            // figures live at `/dashboard` and had no menu entry at all.
            { label: 'Home', path: '/', icon: 'LayoutDashboard', status: R, keywords: 'start things to do tasks' },
            { label: 'Business overview', path: '/dashboard', module: 'dashboard', icon: 'ChartNoAxesCombined', status: R, keywords: 'dashboard figures numbers sales coverage today' },
        ],
    },
    // -------------------------------------------------------------------------
    // The salesmen: their day, their shops, their orders and their expenses.
    {
        label: 'Field sales',
        items: [
            ...fieldNav,
        ],
    },
    // -------------------------------------------------------------------------
    // The stockists: who we supply, what they hold, what they pay, and getting
    // the goods to them. This was scattered across three other sections and one
    // screen of it — Dispatch — was not in the menu at all.
    {
        label: 'Distribution',
        items: [
            ...distributionNav,
        ],
    },
    // -------------------------------------------------------------------------
    // Staff, their attendance, their pay and their records.
    {
        label: 'Staff',
        items: [
            {
                label: 'Everyone', icon: 'Users', status: R, children: [
                    { label: 'Staff at a glance', path: '/hr', module: 'hr.selfservice', icon: 'LayoutDashboard', status: R, keywords: 'hr dashboard notices' },
                    { label: 'My things', path: '/hr/me', module: 'hr.selfservice', icon: 'CircleUser', status: R, keywords: 'payslip my leave' },
                    { label: 'All staff', path: '/hr/employees', module: 'hr.employee', icon: 'Users', status: R, matchPrefix: true, keywords: 'staff' },
                    { label: 'Hiring', path: '/hr/recruitment', module: 'hr.recruitment', icon: 'UserPlus', status: R, keywords: 'candidates hiring' },
                ],
            },
            {
                label: 'Attendance & leave', icon: 'CalendarCheck', status: R, children: [
                    { label: 'Who came in', path: '/hr/attendance', module: 'hr.attendance', status: R },
                    { label: 'Punch', path: '/hr/attendance/punch', module: 'hr.selfservice', status: R, keywords: 'check in' },
                    { label: 'Labour at the gate', path: '/hr/attendance/labour', module: 'hr.attendance', status: R, keywords: 'labour labor supervisor hours photo gate daily wages' },
                    { label: 'Attendance report', path: '/hr/attendance/report', module: 'hr.attendance', status: R },
                    { label: 'Fixing a wrong punch', path: '/hr/attendance/regularisations', module: 'hr.attendance', status: R, keywords: 'regularisation regularization correction missed punch' },
                    { label: 'Rules', path: '/hr/attendance/rules', module: 'master.employee', status: R },
                    { label: 'Punch machines', path: '/hr/attendance/devices', module: 'hr.attendance', status: R },
                    { label: 'Leave', path: '/hr/leave', module: 'hr.leave', icon: 'CalendarDays', status: R },
                ],
            },
            {
                label: 'Pay', icon: 'Wallet', status: R, children: [
                    { label: 'Salary runs', path: '/hr/payroll', module: 'hr.payroll', status: R, keywords: 'salary payslips' },
                    { label: 'Staff expenses', path: '/hr/expenses', module: 'hr.expense', status: R, keywords: 'expenses reimbursement' },
                    { label: 'Loans & advances', path: '/hr/loans', module: 'hr.loan', status: R },
                ],
            },
            {
                label: 'Records', icon: 'TrendingUp', status: R, children: [
                    { label: 'Reviews', path: '/hr/performance', module: 'hr.performance', icon: 'TrendingUp', status: R, keywords: 'appraisal kpi' },
                    { label: 'Training', path: '/hr/training', module: 'hr.training', icon: 'GraduationCap', status: R },
                    { label: 'Letters', path: '/hr/letters', module: 'hr.letter', icon: 'FileSignature', status: R },
                    { label: 'People leaving', path: '/hr/exit', module: 'hr.exit', icon: 'LogOut', status: R, keywords: 'resignation separation' },
                    { label: 'Staff reports', path: '/hr/reports', module: 'hr.report', icon: 'FileBarChart', status: R },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------
    // The lists everything else is built out of.
    {
        label: 'Your lists',
        items: [
            { label: 'Customers & suppliers', path: '/masters/parties', module: 'master.party', icon: 'Contact', status: R, matchPrefix: true, keywords: 'parties customers suppliers vendors ledger' },
            { label: 'Products', path: '/masters/products', module: 'master.product', icon: 'Package', status: R, matchPrefix: true, keywords: 'item goods sku ptr range distribution field products' },
            {
                label: 'Tax', icon: 'Percent', status: R, children: [
                    { label: 'GST rates', path: '/masters/gst-rates', module: 'master.gst', status: R, keywords: 'tax categories cgst sgst igst vat' },
                    { label: 'CESS categories', path: '/field/config/cess-categories', module: 'field.config', status: R, keywords: 'cess tax' },
                    { label: 'HSN / SAC', path: '/masters/hsn', module: 'master.hsn', status: R },
                ],
            },
            {
                label: 'Products & prices', icon: 'Tags', status: R, children: [
                    { label: 'Product groups', path: '/masters/product-groups', module: 'master.product', status: R },
                    { label: 'Units', path: '/masters/units', module: 'master.unit', status: R },
                    { label: 'Price lists', path: '/masters/price-lists', module: 'master.pricelist', status: R },
                    { label: 'Warehouses', path: '/masters/warehouses', module: 'master.warehouse', status: R, keywords: 'godown' },
                    { label: 'Batches', path: '/masters/batches', module: 'master.batch', status: R },
                ],
            },
            /*
             * Both of these had a screen, a server and a permission of their own,
             * and no way in from anywhere in the application -- the same thing
             * that made the leave day settings look as though they did not exist.
             * The reachability check in check-nav now fails on a registered
             * screen nothing offers, which is how these two were found.
             */
            {
                label: 'Costing & production', icon: 'Factory', status: R, children: [
                    { label: 'Cost centres', path: '/masters/cost-centres', module: 'master.costcentre', status: R, keywords: 'department project attribute income expense cost centre' },
                    { label: 'Processes', path: '/masters/processes', module: 'master.process', status: R, keywords: 'manufacturing step job work cost per unit machine' },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------
    // Set up once, changed rarely: the company, who may do what, and the
    // tools for getting data in and checking on it.
    {
        label: 'Settings',
        items: [
            {
                label: 'Company & people', icon: 'Building2', status: R, children: [
                    { label: 'Company', path: '/setup/company', module: 'setup.company', status: R },
                    { label: 'Business years', path: '/setup/financial-years', module: 'setup.fy', status: R },
                    { label: 'Users', path: '/setup/users', module: 'admin.user', icon: 'UserCog', status: R },
                    { label: 'Who may do what', path: '/setup/roles', module: 'admin.role', icon: 'ShieldCheck', status: R, keywords: 'security access rights' },
                    { label: 'Who approves what', path: '/setup/approvals', module: 'setup.approval', status: R },
                    { label: 'General settings', path: '/setup/settings', module: 'setup.software', status: R },
                ],
            },
            {
                label: 'Getting data in and out', icon: 'Wrench', status: R, children: [
                    { label: 'Upload from Excel', path: '/utilities/import', module: 'utility.import', status: R, keywords: 'excel csv upload' },
                    { label: 'Past uploads', path: '/imports', status: R, matchPrefix: true, keywords: 'bulk upload history import jobs job history' },
                    { label: 'Bin', path: '/trash', icon: 'Trash2', status: R, keywords: 'trash deleted recycle restore undo put back recover' },
                    { label: 'Backup', path: '/utilities/backup', module: 'utility.backup', status: R },
                    { label: 'Fixing things', path: '/utilities/advanced', module: 'utility.advanced', status: R, keywords: 'merge renumber' },
                    { label: 'Check for problems', path: '/utilities/health', module: 'utility.repair', status: R, keywords: 'repair verify' },
                    { label: 'Old product categories', path: '/masters/categories', module: 'master.product', status: R, keywords: 'division primary secondary category old three level' },
                ],
            },
            { label: 'Field sales settings', icon: 'Footprints', status: R, children: fieldSetupNav },
            {
                label: 'Staff settings', icon: 'Network', status: R, children: [
                    { label: 'Who reports to whom', path: '/hr/masters/hierarchy', module: 'master.employee', status: R },
                    { label: 'Holidays', path: '/hr/masters/holiday-calendar', module: 'master.employee', status: R },
                    { label: 'Staff lists', path: '/hr/masters/departments', module: 'master.employee', status: R, keywords: 'departments designations shifts leave types salary structures' },
                    { label: 'Who approves staff requests', path: '/hr/admin/approvals', module: 'admin.settings', status: R },
                    { label: 'Staff logins', path: '/hr/admin/users', module: 'admin.user', status: R, keywords: 'employee logins link branch scope' },
                    { label: 'Staff settings', path: '/hr/admin/settings', module: 'admin.settings', status: R },
                    { label: 'Staff activity log', path: '/hr/admin/audit', module: 'admin.audit', status: R },
                ],
            },
            {
                label: 'Keeping an eye on things', icon: 'ScrollText', status: R, children: [
                    { label: 'Who changed what', path: '/utilities/audit', module: 'admin.audit', icon: 'ScrollText', status: R, keywords: 'log history who changed' },
                    { label: 'My notes', path: '/utilities/diary', module: 'utility.diary', status: R, keywords: 'reminder task' },
                ],
            },
        ],
    },
];
// ---------------------------------------------------------------------------
// The menu people see.
// ---------------------------------------------------------------------------
//
// The old menu was a filing cabinet: one drawer per feature family, every
// screen the product has, in the order it happened to be built. It had 146
// entries, 55 of them under Field sales, and it put the screen a salesman
// opens thirty times a day in the same list as the screen that defines what a
// cooler agreement is. Three different jobs are run out of this product — the
// man walking the market, the clerk sending the lorry out, the office running
// the staff — and all three were handed the same cabinet and told to find
// their own drawer.
//
// What people actually ask is not "which feature is this under". It is one of
// five things, and the sections are those five questions:
//
//   TODAY           what I open because the day is happening
//   NEEDS DECIDING  what is sitting waiting on a person
//   HOW IT IS GOING the numbers, when somebody goes looking for them
//   YOUR LISTS      the things the business is made of
//   STAFF           the people
//   SET UP          the answers you give once
//
// The test of it: a salesman never scrolls past a configuration screen to
// reach his day, and nobody has to know which of four places a setting lives
// in, because there is now one.
//
// Every screen is lifted from the pile above by address. Nothing is retyped,
// so nothing can drift; a screen named twice or not at all stops the build,
// which is what the two guards at the bottom are for.
const POOL = new Map();
for (const section of SOURCE_SECTIONS) {
    const walk = (items) => {
        for (const item of items) {
            if (item.path && !item.children?.length) {
                const key = navPathname(item.path);
                if (POOL.has(key))
                    throw new Error(`navigation: two entries declare ${key}`);
                POOL.set(key, item);
            }
            if (item.children)
                walk(item.children);
        }
    };
    walk(section.items);
}
const placed = new Set();
/** Lift a screen out of the pile into its place in the menu, renaming if the new context asks for it. */
function at(path, label) {
    const found = POOL.get(path);
    if (!found)
        throw new Error(`navigation: no screen is declared at ${path}`);
    if (placed.has(path))
        throw new Error(`navigation: ${path} would appear in the menu twice`);
    placed.add(path);
    return label ? { ...found, label } : found;
}
const group = (label, icon, children) => ({
    label, icon, status: R, children,
});
export const NAVIGATION = [
    // The day, as it happens. Flat, because a thing you open thirty times a day
    // should not be behind a heading you have to expand first.
    {
        label: 'Today',
        items: [
            at('/'),
            at('/field/my-day'),
            at('/field/outlets'),
            at('/field/order-requests'),
            at('/field/orders'),
            at('/field/collections'),
            at('/erp/dispatch'),
            at('/field/order-tracking'),
            at('/field/expenses', 'My travel claims'),
            at('/hr/attendance/punch'),
            at('/hr/attendance/labour', 'Mark labour at the gate'),
            at('/hr/attendance'),
            at('/hr/me'),
        ],
    },
    // Everything that is stuck until a person says yes or no. This was spread
    // over four separate groups, which is how a queue goes unnoticed.
    {
        label: 'Needs deciding',
        items: [
            at('/field/approvals'),
            at('/field/requests'),
            at('/field/requests/rewards'),
            at('/field/expenses/approvals', 'Travel claims to approve'),
            at('/field/expenses/edit', 'Change a claim'),
            at('/hr/leave'),
            at('/hr/attendance/regularisations'),
            at('/field/misc/issues'),
        ],
    },
    // Looked at deliberately, not continuously.
    {
        label: 'How it is going',
        items: [
            group('At a glance', 'LayoutDashboard', [
                at('/dashboard', 'The business today'),
                at('/field/dashboard', 'Field sales'),
                at('/hr', 'Staff'),
                at('/field/assets/dashboard', 'Coolers in the market'),
                at('/field/expenses/dashboard', 'Travel money'),
            ]),
            group('Selling', 'BarChart3', [
                at('/field/sales/user-wise'),
                at('/field/retailer-insights'),
                at('/field/schemes/progress'),
                at('/field/schemes/items-consumed'),
            ]),
            group('The team out there', 'Radar', [
                at('/field/discipline/live-map'),
                at('/field/discipline/daily-summary'),
                at('/field/tracking'),
                at('/field/visits'),
                at('/field/attendance', 'Day start and end'),
                at('/field/journey-compliance'),
            ]),
            group('Reports', 'FileBarChart', [
                at('/field/analytics/reports'),
                at('/field/analytics/requests'),
                at('/field/analytics/flexible'),
                at('/field/analytics/quick-viz'),
                at('/field/expenses/reports'),
                at('/hr/attendance/report'),
                at('/hr/reports'),
            ]),
        ],
    },
    // The things the business is made of, rather than the things it does.
    {
        label: 'Your lists',
        items: [
            group('Stockists', 'Store', [
                at('/field/distributors'),
                at('/field/stock'),
                at('/field/distributor-stock'),
                at('/field/portal-logins'),
                at('/masters/parties'),
            ]),
            group('Products & prices', 'Package', [
                at('/masters/products'),
                at('/masters/product-groups'),
                at('/masters/units'),
                at('/masters/batches'),
                at('/masters/price-lists'),
                at('/field/price-lists'),
                at('/field/price-sheets'),
                at('/masters/gst-rates'),
                at('/masters/hsn'),
                at('/field/config/cess-categories'),
            ]),
            group('Costing & production', 'Factory', [
                at('/masters/cost-centres'),
                at('/masters/processes'),
            ]),
            group('Rounds', 'Route', [
                at('/field/beats'),
                at('/field/journey-plans'),
                at('/field/journeys'),
            ]),
            group('Godowns', 'Warehouse', [
                at('/erp/warehouse'),
                at('/masters/warehouses'),
            ]),
            group('Schemes & targets', 'Gift', [
                at('/field/schemes'),
                at('/field/schemes/baskets'),
                at('/field/gtm/targets'),
                at('/field/gtm/flexible-targets'),
                at('/field/misc/kra'),
                at('/field/gtm/must-sell'),
                at('/field/gtm/focus-products'),
            ]),
            group('Coolers & racks', 'Boxes', [
                at('/field/assets'),
                at('/field/assets/types'),
                at('/field/misc/external-assets'),
            ]),
            group('Surveys & training', 'GraduationCap', [
                at('/field/surveys'),
                at('/field/fa-one/learn'),
                at('/field/fa-one/courses'),
                at('/field/fa-one/content'),
                at('/field/fa-one/audits'),
            ]),
        ],
    },
    // The people, and what the business owes them.
    {
        label: 'Staff',
        items: [
            at('/hr/employees'),
            at('/field/users', 'Salesmen'),
            at('/hr/recruitment'),
            at('/hr/payroll'),
            at('/hr/expenses'),
            at('/hr/loans'),
            at('/hr/performance'),
            at('/hr/training'),
            at('/hr/letters'),
            at('/hr/exit'),
        ],
    },
    // Answered once. All of it, in one place: it used to be a top-level
    // Settings, plus Field sales settings, plus Staff settings, plus a Settings
    // inside Distribution, and knowing which was which was its own skill.
    {
        label: 'Set up',
        items: [
            at('/field/start'),
            group('Company & access', 'Building2', [
                at('/setup/company'),
                at('/setup/financial-years'),
                at('/setup/users'),
                at('/setup/roles'),
                at('/setup/approvals'),
                at('/setup/settings'),
            ]),
            group('People & places', 'Network', [
                at('/field/masters/positions'),
                at('/field/masters/geography'),
                at('/field/masters/admins'),
                at('/field/masters'),
                at('/hr/masters/hierarchy'),
                at('/hr/masters/departments'),
                at('/hr/masters/holiday-calendar'),
            ]),
            group('Rules', 'ShieldCheck', [
                at('/field/journey-plans/config'),
                at('/field/journey-plans/capping'),
                at('/field/gtm/segmentation'),
                at('/field/expenses/config'),
                at('/field/expenses/settlements'),
                at('/field/misc/tada-claim'),
                at('/field/config/distribution'),
                at('/field/config/order-blocks'),
                at('/hr/attendance/rules', 'Attendance rules'),
                at('/hr/attendance/devices'),
                at('/hr/admin/approvals'),
            ]),
            group('The phone app', 'Smartphone', [
                at('/field/territories'),
                at('/field/config/outlet-definition'),
                at('/field/config/kpis'),
                at('/field/config/carousel'),
                at('/field/config/retailer-connect'),
                at('/field/config/order-print'),
                at('/field/config/app-workflows'),
                at('/field/config/api-credentials'),
            ]),
            group('Kinds and labels', 'Tags', [
                at('/field/config/shop-types'),
                at('/field/config/sub-shop-types'),
                at('/field/config/channels'),
                at('/field/config/designations'),
                at('/field/config/distributor-segmentation'),
                at('/field/config/distributor-channels'),
                at('/field/config/official-work'),
                at('/field/config/derived-measures'),
                at('/field/config/custom-tags'),
                at('/field/config/holidays'),
                at('/masters/categories'),
            ]),
            group('Staff settings', 'UserCog', [
                at('/hr/admin/users'),
                at('/hr/admin/settings'),
                at('/hr/admin/audit'),
            ]),
            group('Getting data in and out', 'Wrench', [
                at('/utilities/import'),
                at('/imports'),
                at('/trash'),
                at('/utilities/backup'),
                at('/utilities/advanced'),
                at('/utilities/health'),
            ]),
            group('Keeping an eye on things', 'ScrollText', [
                at('/utilities/audit'),
                at('/utilities/diary'),
                at('/field/misc/service-dashboard'),
                at('/field/misc/sync-monitor'),
                at('/field/misc/api-summary'),
            ]),
        ],
    },
];
// Nothing may be lost on the way from the pile to the menu. A screen that was
// declared and then never placed is a screen nobody can reach, and that is a
// thing you discover months later from a user who gave up asking.
{
    const stranded = [...POOL.keys()].filter((p) => !placed.has(p));
    if (stranded.length) {
        throw new Error(`navigation: ${stranded.length} screen(s) declared but in no section: ${stranded.join(', ')}`);
    }
}
/** The path without its query string. */
export function navPathname(path) {
    const q = path.indexOf('?');
    return q === -1 ? path : path.slice(0, q);
}
/** Every leaf item, flattened — for the command palette and consistency checks. */
export function flattenNav(sections = NAVIGATION) {
    const out = [];
    for (const section of sections) {
        const walk = (items, parent) => {
            for (const item of items) {
                if (item.path && !item.children?.length)
                    out.push({ ...item, section: section.label, parent });
                if (item.children)
                    walk(item.children, item.label);
            }
        };
        walk(section.items);
    }
    return out;
}
/** Every declared route path, used to detect collisions at build time. */
export function allNavPaths() {
    return flattenNav().map((i) => navPathname(i.path));
}
/** Find the nav item whose path best matches a location, for breadcrumbs. */
export function findNavItem(pathname) {
    let best;
    let bestLength = -1;
    const consider = (section, item, parent) => {
        if (!item.path)
            return;
        const base = navPathname(item.path);
        const matches = base === pathname || (item.matchPrefix && pathname.startsWith(`${base}/`));
        if (matches && base.length > bestLength) {
            best = { section, item, parent };
            bestLength = base.length;
        }
    };
    const walk = (section, items, parent) => {
        for (const item of items) {
            consider(section, item, parent);
            if (item.children)
                walk(section, item.children, item);
        }
    };
    for (const section of NAVIGATION)
        walk(section, section.items);
    return best;
}
/**
 * The everyday menu.
 *
 * Simple view shows only these. They are the screens a person opens because
 * of something that happened — a customer rang, a lorry arrived, a shift
 * started — rather than the screens someone opens to configure the system or
 * to study it. Everything else is still there, one click away, for anyone who
 * turns Simple view off.
 *
 * Kept as a list of addresses rather than a flag on each entry so the whole
 * everyday menu can be read, and argued about, in one place.
 */
const EVERYDAY_PATHS = new Set([
    '/',
    // What we sell and who we sell it to
    '/masters/parties', '/masters/products',
    // What is on hand
    // People
    '/hr/me', '/hr/attendance', '/hr/leave', '/hr/payroll',
    // The field
    '/field/my-day', '/field/orders', '/field/order-requests', '/field/collections', '/field/visits',
    // What a shop pays and what it earns. Simple view hid both, which made
    // them unfindable rather than simple: the rate is the first question a
    // shopkeeper asks and the scheme is the reason the order is the size it is.
    '/field/price-lists', '/field/schemes',
    // The godowns and what leaves them
    '/erp/warehouse', '/erp/dispatch',
]);
/** Is this one of the screens Simple view keeps? */
export function isEverydayNav(item) {
    if (item.children?.length)
        return item.children.some(isEverydayNav);
    return !!item.path && EVERYDAY_PATHS.has(navPathname(item.path));
}
//# sourceMappingURL=navigation.js.map